import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { Phone, ChatText, EnvelopeSimple, Globe, Star, DownloadSimple, LockSimple, FacebookLogo, InstagramLogo, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BusinessCard() {
  const { oid, slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const base = slug ? `/public/card/slug/${slug}` : `/public/card/${oid}`;

  useEffect(() => {
    api.get(base).then((r) => setC(r.data)).catch((e) => setErr(e.response?.data?.detail || "This card isn't available"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  }, [oid, slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: c?.business_name, url }); return; } catch { /* share cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Card link copied"); } catch { toast.error("Couldn't copy the link"); }
  };

  const API = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10" data-testid="business-card-page">
      <div className="w-full max-w-md space-y-4">
        {err && (
          <div className="card-flat space-y-2 p-8 text-center" data-testid="business-card-error">
            <LockSimple size={40} weight="bold" className="mx-auto text-muted-foreground" />
            <p className="font-heading text-lg font-bold">Card unavailable</p>
            <p className="text-sm text-muted-foreground">{err}</p>
          </div>
        )}
        {!err && !c && <p className="text-center text-muted-foreground">Loading…</p>}
        {c && (
          <div className="card-flat space-y-5 p-6 text-center" data-testid="business-card">
            {c.logo_url && <img src={c.logo_url} alt={c.business_name} className="mx-auto max-h-16" data-testid="card-logo" />}
            <div>
              <p className="font-heading text-2xl font-bold" data-testid="card-name">{c.business_name}</p>
              {c.address && <p className="text-sm text-muted-foreground" data-testid="card-address">{c.address}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {c.phone && <a data-testid="card-call" href={`tel:${c.phone}`} className="flex h-14 flex-col items-center justify-center gap-1 rounded-md bg-primary text-xs font-bold text-primary-foreground"><Phone size={20} weight="bold" />Call</a>}
              {c.phone && <a data-testid="card-text" href={`sms:${c.phone}`} className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-primary text-xs font-bold text-primary"><ChatText size={20} weight="bold" />Text</a>}
              {c.email && <a data-testid="card-email" href={`mailto:${c.email}`} className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-border text-xs font-bold"><EnvelopeSimple size={20} weight="bold" />Email</a>}
            </div>
            {(c.facebook || c.instagram || c.website) && (
              <div className="flex justify-center gap-3" data-testid="card-socials">
                {c.facebook && <a data-testid="card-facebook" href={c.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="flex h-11 w-11 items-center justify-center rounded-full border border-border"><FacebookLogo size={20} weight="bold" /></a>}
                {c.instagram && <a data-testid="card-instagram" href={c.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="flex h-11 w-11 items-center justify-center rounded-full border border-border"><InstagramLogo size={20} weight="bold" /></a>}
                {c.website && <a data-testid="card-website" href={c.website} target="_blank" rel="noreferrer" aria-label="Website" className="flex h-11 w-11 items-center justify-center rounded-full border border-border"><Globe size={20} weight="bold" /></a>}
              </div>
            )}
            <a data-testid="card-vcf" href={`${API}/api${base}/vcf`} className="flex h-14 items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
              <DownloadSimple size={20} weight="bold" /> Save to contacts
            </a>
            {c.google_review_link && (
              <a data-testid="card-review" href={c.google_review_link} target="_blank" rel="noreferrer" className="flex h-14 items-center justify-center gap-2 rounded-md border border-primary font-bold text-primary">
                <Star size={20} weight="fill" /> Leave a Google review
              </a>
            )}
            <div className="space-y-2">
              <img data-testid="card-qr" src={`${API}/api${base}/qr.png`} alt="QR code linking to this card" className="mx-auto w-40 rounded-md border border-border p-2" />
              <p className="text-xs text-muted-foreground">Scan to open this card</p>
            </div>
            <button data-testid="card-share" onClick={share} className="mx-auto flex items-center gap-1 text-xs font-bold text-primary underline">
              <ShareNetwork size={14} weight="bold" /> Share this card
            </button>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground">Powered by RPM Field (Veloktify)</p>
      </div>
    </div>
  );
}
